import { useNotification } from "@/utils/notification";
import { defineStore } from "pinia";
import { Ref, ref } from "vue";
import { ICompanyFilter } from "@/features/company/company.types";
import { ITableHeader } from "@/types/table";
import { companyService } from "@/features/company/company.service";

const { pushNotification } = useNotification();

export const useCompanyStore = defineStore("company", () => {
  const commonFilter = ref<ICompanyFilter>({
    page: 1,
    per_page: 1000,
    search: "",
    provider_id: "",
    team_id: "",
    is_active: null,
    has_team: null,
  });

  const commonSimpleFilter = ref<ICompanyFilter>({
    page: 1,
    per_page: 50,
    search: "",
    provider_id: "",
    team_id: "",
    is_active: null,
    has_team: null,
  });

  const companyHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      fitContent: true,
      type: "centert",
    },
    {
      name: "name",
      label: "Company name",
      width: "35%",
    },
    {
      name: "usdot",
      label: "USDT number",
      width: "10%",
    },
    {
      name: "active_trucks",
      label: "Truck",
      width: "10%",
    },
    {
      name: "team",
      label: "Team",
      width: "15%",
    },
    {
      name: "provider_name",
      label: "App name",
      width: "15%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "10%",
    },
  ];

  const clearFilter = () => {
    commonFilter.value = {
      page: 1,
      per_page: 1000,
      search: "",
      provider_id: "",
      team_id: "",
      is_active: null,
      has_team: null,
    };
  };

  const clearSimpleFilter = () => {
    commonSimpleFilter.value = {
      page: 1,
      per_page: 50,
      search: "",
      provider_id: "",
      team_id: "",
      is_active: null,
      has_team: null,
    };
  };

  async function getCompanies(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await companyService.getCompanies({
        ...commonFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function getSimpleCompanies(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await companyService.getSimpleCompanies({
        ...commonSimpleFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function updateCompany(
    loading: Ref<boolean>,
    companyId: string,
    payload: { team_id: string }
  ) {
    try {
      loading.value = true;
      const { data } = await companyService.updateCompany(companyId, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function bulkUpdateCompany(
    loading: Ref<boolean>,
    payload: {
      company_ids: string[];
      team_id: string;
    }
  ) {
    try {
      loading.value = true;
      const { data } = await companyService.bulkUpdateCompany(payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [];
    } finally {
      loading.value = false;
    }
  }

  async function addCompanyNote(
    loading: Ref<boolean>,
    companyId: string,
    payload: { note: string },
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await companyService
      .addCompanyNote(companyId, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function taskCreationBlock(
    loading: Ref<boolean>,
    companyId: string,
    payload: any,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await companyService
      .taskCreationBlock(companyId, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function truckDeactivation(
    loading: Ref<boolean>,
    companyId: string,
    payload: any,
    cl: (data: unknown) => void
  ) {
    loading.value = true;
    await companyService
      .truckDeactivation(companyId, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    commonFilter,
    commonSimpleFilter,
    companyHeaders,
    clearFilter,
    clearSimpleFilter,
    getCompanies,
    getSimpleCompanies,
    updateCompany,
    bulkUpdateCompany,
    addCompanyNote,
    taskCreationBlock,
    truckDeactivation,
  };
});
